import React from 'react';
import styled from 'styled-components';
import { focus, radius, semantic, space, typeRole, weight } from '@repo/design-tokens';
import type { EntityRef } from '@repo/front-end-utils';

import type { CompositeLoss, CompositeLossEntry } from './compositeLoss';
import { DeadEndGlyph } from './launchFlags';

/** Show enough to recognise what would be lost without turning the dialog into a report. */
const MAX_LISTED = 10;

/**
 * The named, keyboard-reachable list every group in the Launch dialog shows, and the overflow
 * line under it.
 *
 * ONE copy on purpose. `tabIndex` is the load-bearing attribute here: the list scrolls once it
 * exceeds ~6 rows, and a scrolling `<ul>` is not focusable by itself, so without it a keyboard-only
 * user cannot reach the clipped entries before making a destructive decision (WCAG 2.1.1). The
 * design-system Modal's Tab trap reaches any `tabIndex=0` element, so the list sits in the dialog's
 * tab order. (This used to say `TLDialog`'s handler cycled only the action buttons; the check is
 * on the Modal since `onboarding-persistent-scene-launch-modals`.) Two copies of that is two behaviours to keep in step, which is why every group
 * renders through this rather than beside it.
 *
 * The group name sits on a wrapper and the `<ul>` keeps its list role, so a screen reader still
 * says how many items are being lost ("list, 14 items") — `role="group"` on the list itself threw
 * that away.
 */
const CappedList = <T,>({
  items,
  label,
  keyOf,
  render,
}: {
  items: T[];
  label: string;
  keyOf: (item: T) => string;
  render: (item: T) => React.ReactNode;
}) => {
  const listed = items.slice(0, MAX_LISTED);
  const overflow = items.length - listed.length;
  return (
    <div role="group" aria-label={label}>
      <ul tabIndex={0}>
        {listed.map(item => (
          <li key={keyOf(item)}>{render(item)}</li>
        ))}
      </ul>
      {overflow > 0 && <p className="overflow">…and {overflow} more.</p>}
    </div>
  );
};

const EntityList: React.FC<{ entities: EntityRef[]; label: string }> = ({ entities, label }) => (
  <CappedList
    items={entities}
    label={label}
    keyOf={entity => entity.bID}
    render={entity => (
      <>
        {entity.name} <code>{entity.bID}</code>
      </>
    )}
  />
);

export const AREAS_REMOVED_HEADING = 'Areas that will be removed';
export const STOPS_MEASURING_HEADING = 'Things that will stop being measured';
export const TOTALS_LOSING_HEADING = 'Totals that will lose a member';

/**
 * One line per written total: what it is, and how many members it has now and will have. The arrow
 * is for the eye; screen readers voice it inconsistently ("right arrow", or nothing, leaving two bare
 * numbers), so it is hidden from them and said in words instead.
 */
const TotalList: React.FC<{ entries: CompositeLossEntry[] }> = ({ entries }) => (
  <CappedList
    items={entries}
    label={TOTALS_LOSING_HEADING}
    keyOf={entry => entry.target}
    render={entry => (
      <>
        <code>{entry.target}</code>: {entry.from} <span aria-hidden="true">→</span>
        <span className="sr-only"> members now, will have </span> {entry.to}
      </>
    )}
  />
);

/**
 * The body of Launch's one confirmation (`launch-single-confirmation`): what this launch would
 * change that a person should see first.
 *
 * Three groups, each shown only when it has something in it: areas the plan removes, things that
 * stop being measured (an area or machine losing its last camera, including a removed machine that
 * had one), and written totals that lose an area, floor, machine or total member, or are not filed
 * at all (`to` 0). Nothing else is listed: a camera-less machine, walls and floors, automatic totals,
 * a change in how many cameras a total counts, and never-set-up observers launch without asking
 * (Rob, 2026-10-28).
 *
 * It closes by saying the change can be reversed, because activation versions the model and filing
 * keeps the configuration running now.
 */
export const buildLaunchChangesMessage = ({
  twinName,
  areasRemoved,
  entitiesLosing,
  totals,
}: {
  twinName: string;
  areasRemoved: EntityRef[];
  entitiesLosing: EntityRef[];
  totals: CompositeLoss;
}): React.ReactNode => {
  const totalsLosing = [...totals.removed, ...totals.losingMembers].sort((a, b) =>
    a.target.localeCompare(b.target),
  );

  return (
    <RemovalBody>
      {/* Each group is a dead end, so its heading carries the dead-end glyph (`onboarding-status-colours-launch`, R5). */}
      <p>Launching will make these changes to {twinName}.</p>

      {areasRemoved.length > 0 && (
        <>
          <h4 className="group-heading">
            <DeadEndGlyph /> {AREAS_REMOVED_HEADING}
          </h4>
          <EntityList entities={areasRemoved} label={AREAS_REMOVED_HEADING} />
        </>
      )}

      {entitiesLosing.length > 0 && (
        <>
          <h4 className="group-heading">
            <DeadEndGlyph /> {STOPS_MEASURING_HEADING}
          </h4>
          <EntityList entities={entitiesLosing} label={STOPS_MEASURING_HEADING} />
        </>
      )}

      {totalsLosing.length > 0 && (
        <>
          <h4 className="group-heading">
            <DeadEndGlyph /> {TOTALS_LOSING_HEADING}
          </h4>
          <TotalList entries={totalsLosing} />
        </>
      )}

      <p className="closing">
        The current model and measurement setup are kept, so this can be reversed.
      </p>
    </RemovalBody>
  );
};

/**
 * Tokenised throughout. `OnboardingLaunch`'s older blocks carry hard-coded hex under the R5
 * design-system alignment carve-out; that covers what is already there and does not license more.
 */
const RemovalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  p {
    margin: 0;
  }

  /* A real heading per group, so a screen reader can move between what is being lost. */
  .group-heading {
    margin: ${space[2]} 0 0;
    ${typeRole.body2};
    font-weight: ${weight.semibold};
    color: ${semantic.text.primary};
  }

  ul {
    margin: 0;
    padding-left: ${space[4]};
    max-height: 180px;
    overflow-y: auto;
  }

  /*
   * The list is focusable (tabIndex 0) — a control this change adds — so it needs a visible focus
   * indicator, and a token-sourced one (spec AC9.2). focus.tight is the resolved two-layer ring;
   * focus.ring adds a bloom that the dialog's own overflow would clip, and the focusCss.* values
   * carry var() references that would not resolve inside a styled-component.
   *
   * NOTE: no backticks in this comment. It sits INSIDE a styled-components template literal,
   * where a backtick terminates the literal — which is exactly how it broke first time.
   *
   * Shared by every group, because CappedList is.
   */
  ul:focus-visible {
    outline: none;
    box-shadow: ${focus.tight};
    border-radius: ${radius.sm};
  }

  li {
    ${typeRole.body2};
  }

  code {
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  /* Read out, never shown: the words a screen reader says in place of a total line's arrow. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .overflow,
  .closing {
    ${typeRole.body2};
    color: ${semantic.text.secondary};
  }
`;
